<template>
  <main-dialog
    :show="show"
    :title="$t('redeemCode.title')"
    max-width="460px"
    @close="close"
  >
    <div class="redeem-body">
      <p class="text-muted mb-3" style="font-size: 0.9rem;">{{ $t('redeemCode.description') }}</p>

      <b-form-group :label="$t('redeemCode.codeLabel')" label-class="font-weight-bold" class="mb-3">
        <b-input-group>
          <b-form-input
            v-model="code"
            :placeholder="$t('redeemCode.codePlaceholder')"
            :state="validationState"
            :disabled="redeeming || redeemed"
            @keyup.enter="redeem"
            autofocus
            trim
            class="code-input"
          />
        </b-input-group>
        <b-form-invalid-feedback v-if="error">{{ error }}</b-form-invalid-feedback>
      </b-form-group>

      <div v-if="redeemed && result" class="redeem-success mt-3">
        <div class="d-flex align-items-center mb-2">
          <b-icon icon="check-circle-fill" variant="success" font-scale="1.4" class="mr-2" />
          <strong>{{ $t('redeemCode.success') }}</strong>
        </div>
        <div v-if="result.benefits && result.benefits.length > 0" class="benefits-list">
          <div v-for="(b, i) in result.benefits" :key="i" class="benefit-item">
            <b-icon :icon="benefitIcon(b.type)" class="mr-2" />
            <span>{{ benefitText(b) }}</span>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="d-flex w-100 justify-content-end">
        <b-button variant="outline-secondary" @click="close" class="mr-2">
          {{ redeemed ? $t('close') : $t('cancel') }}
        </b-button>
        <b-button
          v-if="!redeemed"
          variant="primary"
          :disabled="!code || redeeming"
          @click="redeem"
        >
          <b-spinner v-if="redeeming" small class="mr-1" />
          {{ redeeming ? $t('redeemCode.redeeming') : $t('redeemCode.redeemButton') }}
        </b-button>
      </div>
    </template>
  </main-dialog>
</template>

<script>
export default {
  name: 'RedeemCodeModal',
  props: {
    show: { type: Boolean, default: false },
  },
  data() {
    return {
      code: '',
      redeeming: false,
      redeemed: false,
      error: null,
      result: null,
    }
  },
  computed: {
    validationState() {
      if (this.error) return false
      if (this.redeemed) return true
      return null
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.code = ''
        this.redeeming = false
        this.redeemed = false
        this.error = null
        this.result = null
      }
    },
  },
  methods: {
    async redeem() {
      if (!this.code || this.redeeming) return
      this.redeeming = true
      this.error = null

      try {
        const resp = await this.$http.post(`/v1/project/codes/${encodeURIComponent(this.code)}/redeem`)
        this.result = resp.data
        this.redeemed = true
        this.$emit('redeemed', resp.data)
      } catch (err) {
        const msg = err.response && err.response.data && err.response.data.message
        this.error = msg || this.$t('redeemCode.failed')
      } finally {
        this.redeeming = false
      }
    },

    close() {
      this.$emit('close')
    },

    benefitIcon(type) {
      switch (type) {
        case 'credits': return 'coin'
        case 'product_free': return 'gift'
        case 'product_discount': return 'percent'
        default: return 'star'
      }
    },

    benefitText(b) {
      var product = b.product_name || b.product_id || ''
      if (b.option_code) product += ' (' + b.option_code + ')'
      switch (b.type) {
        case 'credits':
          return this.$t('redeemCode.benefitCredits', { amount: (b.value || 0).toFixed(2) })
        case 'product_free':
          return this.$t('redeemCode.benefitProductFree', { product: product, months: b.value || 0 })
        case 'product_discount':
          var text = this.$t('redeemCode.benefitProductDiscount', { product: product, percent: b.value || 0 })
          if (b.duration > 0) text += ' ' + this.$t('redeemCode.benefitDuration', { months: b.duration })
          return text
        default:
          return b.type
      }
    },
  },
}
</script>

<style scoped>
.code-input {
  font-family: monospace;
  font-size: 1.05rem;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.redeem-success {
  background: rgba(40, 167, 69, 0.08);
  border: 1px solid rgba(40, 167, 69, 0.25);
  border-radius: 8px;
  padding: 1rem;
}

.benefits-list {
  margin-top: 0.5rem;
}

.benefit-item {
  display: flex;
  align-items: center;
  padding: 0.35rem 0;
  font-size: 0.9rem;
  color: var(--text-primary);
}
</style>
